import { useCallback, useMemo, useRef, useState } from "react";
import { Feather, BookOpen, Compass } from "lucide-react";
import Header from "@/components/Header";
import DailyVerseCard from "@/components/DailyVerseCard";
import QuestionComposer from "@/components/QuestionComposer";
import AnswerView from "@/components/AnswerView";
import DonateModal from "@/components/DonateModal";
import ShareCardModal from "@/components/ShareCardModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uid = () => Math.random().toString(36).slice(2, 10);

export default function AskGodPage() {
  const [question, setQuestion] = useState("");
  const [faith, setFaith] = useState("bible");
  const [entries, setEntries] = useState([]); // [{id, question, faith, answer, status, error}]
  const [loading, setLoading] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [shareEntry, setShareEntry] = useState(null);
  const sessionIdRef = useRef(null);
  const answerRegionRef = useRef(null);

  const historyForApi = useMemo(
    () =>
      entries
        .filter((e) => e.answer)
        .flatMap((e) => [
          { role: "user", content: e.question },
          { role: "assistant", content: e.answer },
        ]),
    [entries]
  );

  const submit = useCallback(
    async (qText) => {
      if (loading) return;
      const id = uid();
      const entry = {
        id,
        question: qText,
        faith,
        answer: "",
        status: "streaming",
        error: null,
      };
      setEntries((prev) => [entry, ...prev]);
      setQuestion("");
      setLoading(true);

      // Scroll to the answer area
      setTimeout(() => {
        answerRegionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);

      try {
        const res = await fetch(`${API}/ask`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: qText,
            faith,
            session_id: sessionIdRef.current,
            history: historyForApi,
          }),
        });

        if (!res.ok || !res.body) {
          const errText = await res.text().catch(() => "");
          throw new Error(errText || `HTTP ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        const applyDelta = (delta) => {
          setEntries((prev) =>
            prev.map((e) => (e.id === id ? { ...e, answer: e.answer + delta } : e))
          );
        };

        // Parse SSE stream: events separated by \n\n
        let streamOpen = true;
        while (streamOpen) {
          const { value, done } = await reader.read();
          if (done) { streamOpen = false; break; }
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf("\n\n")) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const lines = raw.split("\n");
            let eventName = "message";
            let dataStr = "";
            for (const ln of lines) {
              if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
              else if (ln.startsWith("data:")) dataStr += ln.slice(5).trim();
            }
            if (!dataStr) continue;
            try {
              const data = JSON.parse(dataStr);
              if (eventName === "session" && data.session_id) {
                sessionIdRef.current = data.session_id;
              } else if (eventName === "delta" && data.delta) {
                applyDelta(data.delta);
              } else if (eventName === "saved" && data.id) {
                setEntries((prev) =>
                  prev.map((e) => (e.id === id ? { ...e, shareId: data.id } : e))
                );
              } else if (eventName === "error") {
                throw new Error(data.error || "Unknown error");
              }
            } catch (err) {
              // Ignore JSON parse errors on partial chunks
              if (eventName === "error") {
                setEntries((prev) =>
                  prev.map((e) =>
                    e.id === id ? { ...e, status: "error", error: err.message } : e
                  )
                );
              }
            }
          }
        }

        setEntries((prev) =>
          prev.map((e) => (e.id === id ? { ...e, status: "done" } : e))
        );
      } catch (err) {
        setEntries((prev) =>
          prev.map((e) =>
            e.id === id
              ? { ...e, status: "error", error: err.message || "Something went wrong" }
              : e
          )
        );
      } finally {
        setLoading(false);
      }
    },
    [faith, historyForApi, loading]
  );

  return (
    <>
      <Header onOpenDonate={() => setDonateOpen(true)} />

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 relative z-10">
        {/* Hero */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 mb-14 sm:mb-20">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-[#E7B04A] mb-4">
              <Feather className="w-4 h-4" strokeWidth={1.6} />
              <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-sans-clean font-bold">
                A sanctuary of scripture · always free
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#FFFFFF]"
            >
              Ask <em className="text-[#E7B04A]">any question</em>.<br />
              Listen to the scriptures answer.
            </h1>
            <p className="mt-5 max-w-xl text-[#FFFFFF] text-3xl sm:text-xl leading-relaxed font-scripture">
              A quiet space where the Bible, the Qur&rsquo;an, the Torah, the Bhagavad
              Gita, Buddhist wisdom, and the Latter-day Saint scriptures meet the questions of your
              life. No account. No cost. Just the ancient light of many traditions,
              offered gently.
            </p>

            <div className="mt-8">
              <QuestionComposer
                onSubmit={submit}
                loading={loading}
                faith={faith}
                setFaith={setFaith}
                question={question}
                setQuestion={setQuestion}
              />
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-5">
            <DailyVerseCard />
            <div className="rounded-2xl gold-border bg-[#5C0E1D]/70 p-5">
              <div className="flex items-center gap-2 text-[#E7B04A]">
                <Compass className="w-4 h-4" strokeWidth={1.6} />
                <span className="text-xs sm:text-[0.75rem] uppercase tracking-[0.16em] font-sans-clean font-bold">
                  How this works
                </span>
              </div>
              <ol className="mt-3 space-y-3 text-xl sm:text-base text-[#FFFFFF] font-scripture leading-relaxed">
                <li>
                  <span className="text-[#E7B04A] font-bold">1.</span> Choose the
                  tradition(s) you wish to consult — or leave it open to all.
                </li>
                <li>
                  <span className="text-[#E7B04A] font-bold">2.</span> Ask
                  anything: life, love, suffering, purpose, doubt, hope.
                </li>
                <li>
                  <span className="text-[#E7B04A] font-bold">3.</span> Receive a
                  reflection with cited verses you can share.
                </li>
              </ol>
            </div>
          </div>
        </section>

        {/* Answers */}
        <section ref={answerRegionRef} className="space-y-6">
          {entries.length > 0 && (
            <div className="flex items-center gap-2 text-[#E7B04A]">
              <BookOpen className="w-4 h-4" strokeWidth={1.6} />
              <span className="text-xs sm:text-sm uppercase tracking-[0.18em] font-sans-clean font-bold">
                Answers from the Scriptures
              </span>
            </div>
          )}
          {entries.map((entry, i) => (
            <AnswerView
              key={entry.id}
              index={i}
              entry={entry}
              onShare={(e) => setShareEntry(e)}
            />
          ))}

          {entries.length > 0 && !loading && (
            <div className="pt-2">
              <QuestionComposer
                onSubmit={submit}
                loading={loading}
                faith={faith}
                setFaith={setFaith}
                question={question}
                setQuestion={setQuestion}
                compact
              />
            </div>
          )}
        </section>

        {/* Footer */}
        <footer
          data-testid="app-footer"
          id="about"
          className="mt-20 pt-10 border-t border-[#E7B04A]/30 text-[#FBEBD3]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <div className="font-serif-display text-2xl text-[#FFFFFF] mb-2">
                Ask <em className="text-[#E7B04A]">God</em>
              </div>
              <p className="text-xl sm:text-base leading-relaxed font-scripture text-[#FFFFFF]">
                A gift, not a product. Free forever, kept alive by kind hearts.
              </p>
            </div>
            <div>
              <div className="text-xs sm:text-[0.75rem] uppercase tracking-[0.16em] font-bold text-[#E7B04A] mb-2">
                Traditions Honored
              </div>
              <ul className="text-xl sm:text-base space-y-1.5 font-scripture text-[#FFFFFF]">
                <li>Christian Bible · Old &amp; New Testament</li>
                <li>The Holy Qur&rsquo;an</li>
                <li>The Hebrew Torah</li>
                <li>The Bhagavad Gita</li>
                <li>Buddhist Dhammapada &amp; suttas</li>
                <li>Book of Mormon · Doctrine &amp; Covenants · Pearl of Great Price</li>
              </ul>
            </div>
            <div>
              <div className="text-xs sm:text-[0.75rem] uppercase tracking-[0.16em] font-bold text-[#E7B04A] mb-2">
                A Gentle Disclaimer
              </div>
              <p
                data-testid="disclaimer-text"
                className="text-xl sm:text-base leading-relaxed font-scripture italic text-[#FFFFFF]"
              >
                Ask God offers AI-generated reflections drawn from many sacred
                traditions. It is a companion, not a replacement for prayer, community,
                or trained spiritual guides. Please seek human help in crisis.
              </p>
            </div>
          </div>
          <div className="mt-10 text-sm sm:text-sm uppercase tracking-[0.2em] text-center opacity-70">
            &copy; {new Date().getFullYear()} · Ask God · Made with a candle and a keyboard
          </div>
        </footer>
      </main>

      <DonateModal open={donateOpen} onClose={() => setDonateOpen(false)} />
      <ShareCardModal
        open={!!shareEntry}
        onClose={() => setShareEntry(null)}
        question={shareEntry?.question || ""}
        answer={shareEntry?.answer || ""}
        shareId={shareEntry?.shareId}
      />
    </>
  );
}
